/**
 * escalation-params-page.tsx — Escalation Parameter Admin Interface (Story 8.1).
 *
 * staff:admin only (enforced at router level via RequireAuth + at API level).
 *
 * ACs covered:
 * - AC 1: list all triggers with name, type, tier, cooldown, status, version
 * - AC 2: create/update triggers; MD assignment panel visible
 * - AC 3: version history modal per trigger
 * - AC 4: create trigger persisted with correct schema
 * - AC 5: update trigger persisted, old values in version log
 * - AC 6: MD tier requires confirmation gate (in TriggerForm)
 * - AC 7: cluster trigger minimumCoOccurrenceCount >= 2 validated
 * - AC 8: MD assignment CRUD
 * - AC 9: disable trigger (soft-disable)
 * - AC 10: cooldown 1–720h, default 24h
 */

import { usePortalAuth } from '@nrx/auth-ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';

import {
  type EscalationDestinationTier,
  type EscalationParamVersion,
  type EscalationTriggerConfig,
  type EscalationTriggerRecord,
  type EscalationTriggerType,
  type MdAssignmentRecord,
  createEscalationTrigger,
  disableEscalationTrigger,
  enableEscalationTrigger,
  getMdAssignment,
  getTriggerVersionHistory,
  listEscalationTriggers,
  setMdAssignment,
  updateEscalationTrigger,
} from './escalation-params-api.js';
import { MdAssignmentPanel } from './md-assignment-panel.js';
import { TriggerForm } from './trigger-form.js';
import { TriggerList } from './trigger-list.js';
import { TriggerVersionHistoryModal } from './trigger-version-history-modal.js';

type ViewMode = 'list' | 'create' | 'edit';

export function EscalationParamsPage(): JSX.Element {
  const auth = usePortalAuth();
  const accessToken = auth.user?.access_token ?? '';

  // Trigger list state
  const [triggers, setTriggers] = useState<EscalationTriggerRecord[]>([]);
  const [triggersLoading, setTriggersLoading] = useState(true);
  const [triggersError, setTriggersError] = useState<string | null>(null);

  // MD assignment state
  const [mdAssignment, setMdAssignmentState] = useState<MdAssignmentRecord | null>(null);
  const [mdSaving, setMdSaving] = useState(false);
  const [mdSaveError, setMdSaveError] = useState<string | null>(null);

  // Form view state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingTrigger, setEditingTrigger] = useState<EscalationTriggerRecord | null>(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formSaveError, setFormSaveError] = useState<string | null>(null);

  // Disable state
  const [disabling, setDisabling] = useState<string | null>(null);
  const [disableError, setDisableError] = useState<string | null>(null);

  // Version history state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTrigger, setHistoryTrigger] = useState<EscalationTriggerRecord | null>(null);
  const [history, setHistory] = useState<EscalationParamVersion[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const loadTriggers = useCallback(async (): Promise<void> => {
    if (!accessToken) return;
    setTriggersLoading(true);
    setTriggersError(null);
    try {
      const result = await listEscalationTriggers(accessToken);
      setTriggers(result);
    } catch {
      setTriggersError('Failed to load escalation triggers. Please refresh the page.');
    } finally {
      setTriggersLoading(false);
    }
  }, [accessToken]);

  const loadMdAssignment = useCallback(async (): Promise<void> => {
    if (!accessToken) return;
    try {
      const result = await getMdAssignment(accessToken);
      setMdAssignmentState(result);
    } catch {
      // Non-fatal
    }
  }, [accessToken]);

  useEffect(() => {
    void loadTriggers();
    void loadMdAssignment();
  }, [loadTriggers, loadMdAssignment]);

  function handleCreateClick(): void {
    setEditingTrigger(null);
    setFormSaveError(null);
    setViewMode('create');
  }

  function handleEditClick(trigger: EscalationTriggerRecord): void {
    setEditingTrigger(trigger);
    setFormSaveError(null);
    setViewMode('edit');
  }

  function handleFormCancel(): void {
    setViewMode('list');
    setEditingTrigger(null);
    setFormSaveError(null);
  }

  async function handleFormSave(
    data: {
      name: string;
      type: EscalationTriggerType;
      config: EscalationTriggerConfig;
      destinationTier: EscalationDestinationTier;
      cooldownHours: number;
      mdTierConfirmed?: boolean;
    },
    triggerId?: string,
  ): Promise<void> {
    setFormSaving(true);
    setFormSaveError(null);
    try {
      if (triggerId) {
        await updateEscalationTrigger(accessToken, triggerId, {
          name: data.name,
          type: data.type,
          config: data.config,
          destinationTier: data.destinationTier,
          cooldownHours: data.cooldownHours,
          ...(data.mdTierConfirmed !== undefined ? { mdTierConfirmed: data.mdTierConfirmed } : {}),
        });
      } else {
        await createEscalationTrigger(accessToken, {
          name: data.name,
          type: data.type,
          config: data.config,
          destinationTier: data.destinationTier,
          cooldownHours: data.cooldownHours,
          ...(data.mdTierConfirmed !== undefined ? { mdTierConfirmed: data.mdTierConfirmed } : {}),
        });
      }
      setViewMode('list');
      setEditingTrigger(null);
      await loadTriggers();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save trigger. Please try again.';
      setFormSaveError(message);
      throw err;
    } finally {
      setFormSaving(false);
    }
  }

  async function handleDisable(triggerId: string): Promise<void> {
    setDisabling(triggerId);
    setDisableError(null);
    try {
      await disableEscalationTrigger(accessToken, triggerId);
      await loadTriggers();
    } catch {
      setDisableError('Failed to disable trigger. Please try again.');
    } finally {
      setDisabling(null);
    }
  }

  async function handleEnable(triggerId: string): Promise<void> {
    setDisabling(triggerId);
    setDisableError(null);
    try {
      await enableEscalationTrigger(accessToken, triggerId);
      await loadTriggers();
    } catch {
      setDisableError('Failed to re-enable trigger. Please try again.');
    } finally {
      setDisabling(null);
    }
  }

  async function handleViewHistory(trigger: EscalationTriggerRecord): Promise<void> {
    setHistoryTrigger(trigger);
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError(null);
    setHistory([]);
    try {
      const result = await getTriggerVersionHistory(accessToken, trigger.id);
      setHistory(result);
    } catch {
      setHistoryError('Failed to load version history.');
    } finally {
      setHistoryLoading(false);
    }
  }

  function handleHistoryClose(): void {
    setHistoryOpen(false);
    setHistoryTrigger(null);
    setHistory([]);
  }

  async function handleMdAssignmentSave(staffSub: string, staffName: string): Promise<void> {
    setMdSaving(true);
    setMdSaveError(null);
    try {
      await setMdAssignment(accessToken, staffSub, staffName);
      await loadMdAssignment();
    } catch {
      setMdSaveError('Failed to save MD assignment. Please try again.');
      throw new Error('Save failed');
    } finally {
      setMdSaving(false);
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Escalation Parameters</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure clinical escalation triggers and routing — staff:admin only
          </p>
        </div>
        {viewMode === 'list' && (
          <button
            type="button"
            onClick={handleCreateClick}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Add Trigger
          </button>
        )}
      </div>

      {viewMode === 'list' ? (
        <div className="space-y-6">
          <MdAssignmentPanel
            assignment={mdAssignment}
            onSave={(sub, name) => handleMdAssignmentSave(sub, name)}
            saving={mdSaving}
            saveError={mdSaveError}
          />

          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-base font-medium text-gray-800">Escalation Triggers</h2>
            </div>
            {triggersError && <p className="px-4 py-4 text-red-600 text-sm">{triggersError}</p>}
            {disableError && <p className="px-4 py-2 text-amber-700 text-sm">{disableError}</p>}
            {!triggersError && (
              <TriggerList
                triggers={triggers}
                onEdit={handleEditClick}
                onDisable={(id) => void handleDisable(id)}
                onEnable={(id) => void handleEnable(id)}
                onViewHistory={(trigger) => void handleViewHistory(trigger)}
                disabling={disabling}
                loading={triggersLoading}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-medium text-gray-800">
              {viewMode === 'create'
                ? 'New Escalation Trigger'
                : `Edit: ${editingTrigger?.name ?? ''}`}
            </h2>
            <button
              type="button"
              onClick={handleFormCancel}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              ← Back to list
            </button>
          </div>

          <TriggerForm
            {...(editingTrigger ? { initial: editingTrigger } : {})}
            onSave={(data, triggerId) => handleFormSave(data, triggerId)}
            onCancel={handleFormCancel}
            saving={formSaving}
            saveError={formSaveError}
          />
        </div>
      )}

      <TriggerVersionHistoryModal
        open={historyOpen}
        triggerName={historyTrigger?.name ?? ''}
        history={history}
        loading={historyLoading}
        error={historyError}
        onClose={handleHistoryClose}
      />
    </div>
  );
}
